import {
  takeLatest, select, call, all,
  takeEvery,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import { AWS_GATOR_SOLUTIONS_URL, config } from '@/config';
import { isBRServer } from '@/utils/Validators/validation';
import logger from '@/utils/logger';
import { MerlinActionTypes } from '@/redux/modules/merlin/merlin.types';
import { put } from 'redux-saga-test-plan/matchers';
import { commonActions, merlinActions } from '@/redux/modules';
import React from 'react';
import { locale } from '@/utils/locale';


const { WHMCS_URL } = config;

function* requestMerlinToken() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/ai-agents/generate-token`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };
  try {
    const response = yield call(api.whmcs.get, request);
    yield put(merlinActions.merlinTokenSuccess(response.data.data.token));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    yield put(merlinActions.merlinTokenFail());
    logger.error('Error sagas requestMerlinToken', error);
  }
}

function* sendAudioMessage(data) {
  const {
    file, token, accountId, threadId, env, brand, url,
  } = data.payload;

  const urlProtocol = 'https://';
  const connectionUrl = `${urlProtocol}flow.hostgator.com.br`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('token', token);
  formData.append('accountId', accountId);
  formData.append('threadId', threadId);
  formData.append('env', env);
  formData.append('brandName', brand);
  formData.append('url', url);

  const request = {
    url: `${connectionUrl}/ai-agents/chat/transcribe-audio`,
    data: formData,
  };

  yield put(merlinActions.inhouse.setLoadingTranscription(true));
  try {
    const response = yield call(api.n8n.post, request);

    yield put(merlinActions.inhouse.getAudioTranscription({
      result: response.data.result,
    }));
  } catch (requestError) {
    yield put(merlinActions.inhouse.setLoadingTranscription(false));
  }
}

function* aiChatRateMessage(data) {
  const {
    url,
    env,
    threadId,
    accountId,
    token,
    messageId,
    rating,
    apiAuthKey,
    brandName,
  } = data.payload;

  const urlProtocol = 'https://';
  const connectionUrl = `${urlProtocol}flow.hostgator.com.br/ai-agents/chat/rate-message`;

  const request = {
    url: connectionUrl,
    data: {
      url,
      env,
      threadId,
      accountId,
      token,
      messageId,
      rating,
      apiAuthKey,
      brandName,
    },
  };

  try {
    yield call(api.n8n.post, request);
    yield put(merlinActions.inhouse.successRateMessage());
  } catch (requestError) {
    yield put(merlinActions.inhouse.failureRateMessage());
  }
}

function* aiChatRating(data) {
  const {
    url,
    env,
    threadId,
    token,
    rating,
    ratingMessage,
    brandName,
  } = data.payload;

  const urlProtocol = 'https://';
  const connectionUrl = `${urlProtocol}flow.hostgator.com.br/ai-agents/chat/rate-chat`;

  const request = {
    url: connectionUrl,
    data: {
      url,
      env,
      threadId,
      token,
      rating,
      ratingMessage,
      brandName,
    },
  };

  try {
    yield call(api.n8n.post, request);
    yield put(merlinActions.inhouse.successRateMessage());
  } catch (requestError) {
    yield put(merlinActions.inhouse.failureRateMessage());
  }
}

function* redirectToBrizy({ payload }) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { id: clientId } = yield select(state => state.summary);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/ai-agents/client/${clientId}/builder/${payload.builderId}/domain/${payload.domain}/redirectsso`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
  };


  yield put(merlinActions.executeN8nLoading({ bool: true, index: payload.index }));
  try {
    const response = yield call(api.whmcs.get, request);
    if (response) {
      window.open(response.data.data.url, '_blank');
    }
    yield put(merlinActions.executeN8nLoading({ bool: false, index: undefined }));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas common - method executeN8nDispatch', error);
    yield put(merlinActions.executeN8nLoading({ bool: false, index: undefined }));
  }
}

function* requestAiAlerts() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/client/alerts`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
      'x-client-id': userId,
    },
  };

  yield put(merlinActions.alerts.loadingAlerts(true));
  try {
    const response = yield call(api.aws.get, request);
    const alerts = response.data;

    yield put(merlinActions.alerts.setAlerts({ alerts }));

    yield put(merlinActions.alerts.loadingAlerts(false));
  } catch (e) {
    const error = { ...e, userRequestData: request };
    if (e.response && e.response.status !== 401) {
      logger.error('Error sagas requestAiAlerts', error);
    }
    yield put(merlinActions.alerts.loadingAlerts(false));
  }
}

function* requestAiAlertsTypes() {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/alerts/types`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
      'x-client-id': userId,
    },
  };

  yield put(merlinActions.alerts.loadingAlertsTypes(true));
  try {
    const response = yield call(api.aws.get, request);
    const alertsArray = response.data;

    const alertsTypes = {};

    alertsArray.forEach((item) => {
      const newItem = {
        ...item,
        loaded: false,
        loading: false,
      };

      alertsTypes[item.slug] = newItem;
    });

    yield put(merlinActions.alerts.setAlertsTypes({ alertsTypes }));
    yield put(merlinActions.alerts.loadingAlertsTypes(false));
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas requestAiAlerts', error);
    yield put(merlinActions.alerts.loadingAlertsTypes(false));
  }
}

function* requestAiAlertPerSlug({ payload }) {
  const { slug } = payload;
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/alerts/${slug}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
      'x-client-id': userId,
    },
  };

  yield put(merlinActions.alerts.loadingAlerts(true));
  yield put(merlinActions.alerts.setLoadingBySlug({ slug, loading: true }));
  try {
    const response = yield call(api.aws.get, request);
    const alert = response.data[0] || { type: { slug }, elements: null };

    yield put(merlinActions.alerts.setAlerts({ alert }));
    yield put(merlinActions.alerts.loadingAlerts(false));
  } catch (e) {
    const error = { ...e, userRequestData: request };
    if (e.response && e.response.status !== 401) {
      logger.error('Error sagas requestAiAlerts', error);
    }
    yield put(merlinActions.alerts.setAlerts({ alert: { type: { slug }, elements: null } }));
    yield put(merlinActions.alerts.loadingAlerts(false));
    yield put(merlinActions.alerts.setLoadingBySlug({ slug, loading: false }));
  }
}

function* removeAiAlerts({ payload }) {
  const alertsTypes = yield select(state => state.merlin.alertsTypes);
  const alertsSlugs = alertsTypes ? Object.keys(alertsTypes) : [];

  yield put(merlinActions.alerts.loadingAlerts(true));

  const {
    delay, alerts, nonRemovableTitle, undo,
  } = payload;


  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${WHMCS_URL}/api/v3/alerts/remove`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
    data: {
      delay,
      alerts,
    },
  };

  if (undo) {
    request.data.undo = true;
    delete request.data.delay;
  }

  const notRemovableTitleString = (() => {
    const titles = nonRemovableTitle && nonRemovableTitle.map(item => `"${item.title}"`);
    if (!titles) return '';
    if (titles.length === 1) return titles[0];
    if (titles.length === 2) return titles.join(isBRServer ? ' e ' : 'y ');
    return `${titles.slice(0, -1).join(', ')} e ${titles[titles.length - 1]}`;
  })();

  try {
    yield call(api.whmcs.post, request);

    if (nonRemovableTitle.length > 0 && !delay && !undo) {
      yield put(commonActions.notifications.set({
        label: locale('aichat.alerts.toast.notAllowed')({ list: notRemovableTitleString, amount: nonRemovableTitle.length }),
        type: 'warning',
      }));
    }

    if (!undo) {
      yield put(commonActions.notifications.set({
        label: (
          <>
            {delay ? locale('aichat.alerts.toast.successDelay') : locale('aichat.alerts.toast.successDelete')}
          </>
        ),
        type: 'info',
      }));
    }
    yield all(
      alertsSlugs.map(slug => put(merlinActions.alerts.requestAlertPerSlug({ slug }))),
    );
  } catch (e) {
    const error = { ...e };
    if (e.response && e.response.status !== 401) {
      logger.error('Error sagas removeAiAlerts', error);
    }
  }
}

function* sagas() {
  yield takeLatest(MerlinActionTypes.MERLIN_GENERATE_TOKEN, requestMerlinToken);
  yield takeLatest(MerlinActionTypes.SEND_AUDIO_MESSAGE, sendAudioMessage);
  yield takeLatest(MerlinActionTypes.AI_CHAT_RATE_MESSAGE, aiChatRateMessage);
  yield takeLatest(MerlinActionTypes.RATE_AI_CHAT, aiChatRating);
  yield takeLatest(MerlinActionTypes.REDIRECT_TO_BRIZY, redirectToBrizy);
  yield takeLatest(MerlinActionTypes.AI_REQUEST_ALERTS, requestAiAlerts);
  yield takeLatest(MerlinActionTypes.AI_REMOVE_ALERTS, removeAiAlerts);
  yield takeLatest(MerlinActionTypes.AI_GET_ALERTS_TYPES, requestAiAlertsTypes);
  yield takeEvery(MerlinActionTypes.AI_REQUEST_ALERT_PER_SLUG, requestAiAlertPerSlug);
}

export default sagas;
